// src/pages/HomePage.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { usePageMeta } from '../hooks/usePageMeta';

// карты
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// slider (для блока проектов)
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

/* ---------- utils ---------- */
// уважение prefers-reduced-motion
function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduce(!!mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, []);
  return reduce;
}

/* ---------- animations ---------- */
const float = keyframes`
  0%   { transform: translateY(0) }
  50%  { transform: translateY(-6px) }
  100% { transform: translateY(0) }
`;

const iconBox = css`
  width: var(--icon-size, 48px);
  height: var(--icon-size, 48px);
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: ${({theme})=>theme.body};
  border: 1px solid ${({theme})=>theme.border};
  svg{
    width: 24px;
    height: 24px;
  }
`;
const shimmer = keyframes`
  0% { background-position: 0% 50% }
  100% { background-position: 200% 50% }
`;

// Hero key frames
const blobAnim = keyframes`
  0% { transform: translate(0,0) scale(1); opacity:.8 }
  50% { transform: translate(12px,-10px) scale(1.05); opacity:.95 }
  100% { transform: translate(0,0) scale(1); opacity:.8 }
`;
const drift = keyframes`
  0%   { transform: translateX(-2%); }
  50%  { transform: translateX(2%); }
  100% { transform: translateX(-2%); }
`;
const pulse = keyframes`
  0%,100% { transform: translateY(0); opacity:.9 }
  50%     { transform: translateY(-4px); opacity:1 }
`;

/* ---------- styles ---------- */

// Hero — с отключаемой анимацией через [data-motion="off"]
const Hero = styled.section`
  position: relative;
  padding: clamp(72px, 10vw, 140px) 0;
  overflow: hidden;
  isolation: isolate;

  /* если motion off — рубим декоративные анимации */
  &[data-motion="off"] .bg,
  &[data-motion="off"] .blob,
  &[data-motion="off"] .chip svg,
  &[data-motion="off"] .mock,
  &[data-motion="off"] .card {
    animation: none !important;
  }

  .bg {
    position: absolute; inset: -10%;
    background:
      radial-gradient(40% 40% at 15% 10%, #ff660022, transparent 60%),
      radial-gradient(45% 45% at 85% 15%, #00c2ff1e, transparent 60%),
      radial-gradient(55% 55% at 50% 110%, #a8ff0014, transparent 60%);
    filter: blur(40px);
    animation: ${drift} 12s ease-in-out infinite;
    z-index: -1;
  }
  .blob {
    position:absolute; width:220px; height:220px; border-radius:50%;
    background: radial-gradient(circle at 30% 30%, #ff660033, transparent 60%);
    filter: blur(24px);
    animation:${blobAnim} 9s ease-in-out infinite;
  }
  .blob.b2{ right:10%; top:20%; background: radial-gradient(circle at 70% 30%, #00c2ff2e, transparent 60%); animation-duration: 11s; }
  .blob.b3{ left:50%; bottom:-30px; background: radial-gradient(circle at 50% 50%, #a8ff0026, transparent 60%); animation-duration: 13s; }

  .wrap {
    position: relative;
    width: ${({theme})=>theme.container}; margin: 0 auto;
    display: grid; grid-template-columns: 1.15fr .85fr; gap: clamp(20px, 4vw, 48px);
    align-items: center;
  }
  @media (max-width: 980px) { .wrap { grid-template-columns: 1fr; } }

  h1 { font-size: ${({theme})=>theme.font.sizes.h1}; line-height:1.02; margin-bottom: 10px; }
  p.lead { font-size: clamp(18px, 2.2vw, 22px); color: ${({theme})=>theme.textMuted}; margin: 0 0 18px; }
  .cta { display:flex; gap: 10px; flex-wrap: wrap; }

  .directions { margin-top: 22px; display:flex; flex-wrap:wrap; gap: 10px; }
  .chip {
    display:inline-flex; align-items:center; gap:8px;
    padding: 10px 12px; border-radius: ${({theme})=>theme.radius.pill}px;
    border: 1px solid ${({theme})=>theme.border};
    background: ${({theme})=>theme.surface};
    box-shadow: ${({theme})=>theme.shadow.xs};
    transition: transform .2s ease;
    will-change: transform; color:#ff6600;
  }
  .chip:hover{ transform: translateY(-2px); }
  .chip svg{ width:24px; height:24px; display:block; animation:${pulse} 3s ease-in-out infinite; }

  .mock {
    width: min(560px, 90%); aspect-ratio: 16/10; margin: auto;
    border-radius: 18px; border: 1px solid ${({theme})=>theme.border};
    background:
      linear-gradient(120deg, #ffffff10, #ffffff00 40%) padding-box,
      linear-gradient(90deg,#ff660055,#00c2ff55,#a8ff0055) border-box;
    background-size: 200% 100%;
    animation: ${shimmer} 5s linear infinite;
    position: relative; box-shadow: ${({theme})=>theme.shadow.lg};
    overflow: hidden; display: grid; place-items: center;
  }
  .mock-inner{
    width: 92%; height: 86%;
    border-radius: 12px; background: ${({theme})=>theme.body};
    border: 1px dashed ${({theme})=>theme.border};
    padding: 14px; display:grid; align-content:center; gap:10px; text-align:center;
    color: ${({theme})=>theme.textMuted}; font-size: 14px;
  }
`;

const QuickQuote = styled.section`
  padding: 0 0 18px;
  .bar{
    width: ${({theme})=>theme.container}; margin: -18px auto 0;
    display:flex; gap: 10px; align-items:center; justify-content:space-between; flex-wrap:wrap;
    padding: 12px 16px;
    border: 1px dashed ${({theme})=>theme.border};
    background: linear-gradient(90deg, rgba(255,102,0,.08), transparent 70%);
    border-radius: ${({theme})=>theme.radius.lg}px;
  }
  .row{display:flex; gap:8px; flex-wrap:wrap}
`;

const Section = styled.section`
  padding: 72px 0;
  &.muted { background: ${({theme})=>theme.body}; }
  .container { width: ${({theme})=>theme.container}; margin: 0 auto; }
  .section-head {
    display:flex; align-items:end; justify-content:space-between; gap: 16px; margin-bottom: 24px;
  }
  .kicker { font-size: 14px; opacity: .7; text-transform: uppercase; letter-spacing: .12em; }
`;

const Services = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
  .card {
    padding: 18px; border: 1px solid ${({theme})=>theme.border};
    background: ${({theme})=>theme.surface}; border-radius: ${({theme})=>theme.radius.lg}px;
    box-shadow: ${({theme})=>theme.shadow.sm}; position: relative; overflow: hidden;
  }
  .icon {
    ${iconBox};
    margin-bottom: 10px;
    animation: ${float} 4s ease-in-out infinite;
  }
  .card h3 { margin-bottom: 6px; }
  .card p { color: ${({theme})=>theme.textMuted}; margin-bottom: 12px; }
`;

const SystemsTeaser = styled.div`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 14px;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
  .card{
    padding:16px; border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.lg}px;
    box-shadow:${({theme})=>theme.shadow.sm}; display:flex; flex-direction:column; gap:8px;
  }
  .icon{
    ${iconBox};
    margin-bottom: 8px;
  }
  .card p{ color:${({theme})=>theme.textMuted}; margin:0; }
`;

const WhyUs = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px;
  @media (max-width:980px){ grid-template-columns: 1fr; }
  .w{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px; position:relative; overflow:hidden;
    transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
  }
  .w:hover{ transform: translateY(-3px); box-shadow:${({theme})=>theme.shadow.md}; border-color:${({theme})=>theme.accent}; }
  .ico{
    ${iconBox};
    margin-bottom:8px;
  }
  p{ margin:6px 0 0; color:${({theme})=>theme.textMuted}; }
`;

const KPIs = styled.div`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap:12px;
  @media (max-width:980px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  .k{
    padding:18px; text-align:center; border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.lg}px;
  }
  .k b{ font-size: clamp(20px, 2.6vw, 28px); display:block; }
  .k span{ color:${({theme})=>theme.textMuted}; font-size:14px; }
`;

const Offers = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:14px;
  @media (max-width:980px){ grid-template-columns: 1fr; }
  .o{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px; display:flex; flex-direction:column; gap:8px;
  }
  .price{ font-weight:800; font-size:18px; }
  ul{ margin:0; padding-left:18px; color:${({theme})=>theme.textMuted}; }
`;

const BlogTeasers = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px;
  @media (max-width:980px){ grid-template-columns: 1fr; }
  .b{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden; display:flex; flex-direction:column;
  }
  .cover{
    aspect-ratio: 16/9;
    width:100%;
    object-fit:cover;
    background:${({theme})=>theme.body};
  }
  .cap{ padding:12px; display:grid; gap:6px; }
  .cap p{ color:${({theme})=>theme.textMuted}; margin:0; }
`;

const BuyGrid = styled.div`
  display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap:16px;
  @media (max-width:820px){ grid-template-columns: 1fr; }
  .col{
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px;
    box-shadow:${({theme})=>theme.shadow.sm};
    padding:16px;
  }
  .col .icon{
    ${iconBox};
    margin-bottom:8px;
  }
  .col h3{ margin:0 0 8px; }
  .tags{ display:flex; flex-wrap:wrap; gap:8px; }
  .tag{
    padding:8px 10px;
    border:1px solid ${({theme})=>theme.border};
    border-radius:999px;
    background:${({theme})=>theme.body};
    font-size:14px;
    transition: transform .2s ease;
  }
  .tag:hover{ transform: translateY(-1px); }
`;

const Guarantees = styled.div`
  display:grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap:12px;
  @media (max-width:980px){ grid-template-columns: 1fr; }
  .g{
    border:1px dashed ${({theme})=>theme.border}; background:${({theme})=>theme.body};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px;
  }
  .g b{ display:block; margin-bottom:6px; }
`;

const Partners = styled.div`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px;
  @media (max-width: 980px){ grid-template-columns: repeat(2, minmax(0,1fr)); }
  .partner {
    padding: 16px; border: 1px dashed ${({theme})=>theme.border};
    background: ${({theme})=>theme.surface}; border-radius: ${({theme})=>theme.radius.md}px;
    color: ${({theme})=>theme.textMuted}; text-align:center; min-height: 68px;
    display:grid; place-items:center;
  }
`;

const Steps = styled.ol`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 14px;
  counter-reset: s; margin: 0; padding: 0; list-style: none;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
  li{
    counter-increment: s;
    border: 1px solid ${({theme})=>theme.border}; background: ${({theme})=>theme.surface};
    border-radius: ${({theme})=>theme.radius.lg}px; padding: 18px; box-shadow: ${({theme})=>theme.shadow.sm};
  }
  li b::before{ content: counter(s) '. '; color: ${({theme})=>theme.accent}; font-weight: 800; }
  li p{ margin: 6px 0 0 0; color: ${({theme})=>theme.textMuted}; }
`;

const CTA = styled.section`
  padding: 56px 0;
  .box {
    width: ${({theme})=>theme.container}; margin: 0 auto;
    border: 1px solid ${({theme})=>theme.border};
    background: linear-gradient( to right, #ff660022, transparent 60%);
    border-radius: ${({theme})=>theme.radius.lg}px; padding: 24px;
    display:flex; align-items:center; justify-content:space-between; gap: 16px; flex-wrap: wrap;
  }
  .btn { min-width: 180px; }
`;

const ProjCard = styled.div`
  border:1px solid ${({theme})=>theme.border};
  background:${({theme})=>theme.surface};
  border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
  img{ width:100%; height:240px; object-fit:cover; display:block; }
  .cap{ padding:12px 14px; display:flex; align-items:center; justify-content:space-between; gap:8px; }
  .cap b{ font-size:14px; }
`;

const Industries = styled.div`
  display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 14px;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
  .i{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; padding:16px;
    position:relative; overflow:hidden; transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
    box-shadow:${({theme})=>theme.shadow.sm};
  }
  .i::after{
    content:''; position:absolute; inset:-40%; background: radial-gradient(30% 30% at 20% 0%, #ff660022, transparent 60%);
    filter: blur(30px); transform: translateY(10px); transition: opacity .3s ease, transform .3s ease; opacity:.6; pointer-events:none;
  }
  .i:hover{ transform: translateY(-4px); box-shadow:${({theme})=>theme.shadow.md}; border-color:${({theme})=>theme.accent}; }
  .icon{
    ${iconBox};
    margin-bottom:8px;
  }
  .i p{ margin:6px 0 0; color:${({theme})=>theme.textMuted}; }
`;

const FaqBlock = styled.div`
  display:grid; gap:10px;
  .q{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
  }
  .q button{
    width:100%; text-align:left; padding:14px; background:none; border:none; cursor:pointer;
    display:flex; align-items:center; justify-content:space-between; gap:8px; font-weight:700;
  }
  .a{ padding:0 14px 14px; color:${({theme})=>theme.textMuted}; }
`;

const MiniForm = styled.form`
  display:grid; grid-template-columns: 1.2fr 1fr auto; gap:10px;
  @media (max-width: 980px){ grid-template-columns: 1fr; }
  input, textarea{
    border:1px solid ${({theme})=>theme.border}; background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.md}px; padding:10px 12px; color:inherit;
  }
  textarea{ grid-column: 1 / -2; min-height: 44px; }
  .btn{ grid-column: -2 / -1; }
  @media (max-width: 980px){
    textarea{ grid-column: 1 / -1; }
    .btn{ grid-column: 1 / -1; }
  }
`;

const Certs = styled.div`
  display:flex; gap:10px; flex-wrap:wrap;
  .c{
    padding:10px 12px; border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface}; border-radius:${({theme})=>theme.radius.pill}px;
    font-size:14px;
  }
`;

const MapWrap = styled.section`
  padding: 64px 0 80px;
  background: ${({theme})=>theme.body};
  .wrap { width: ${({theme})=>theme.container}; margin: 0 auto; display:grid; grid-template-columns: 360px 1fr; gap: 16px; }
  @media (max-width: 980px){ .wrap{ grid-template-columns: 1fr; } }
  .panel{
    border:1px solid ${({theme})=>theme.border};
    background:${({theme})=>theme.surface};
    border-radius:${({theme})=>theme.radius.lg}px;
    padding:16px; box-shadow:${({theme})=>theme.shadow.sm};
    display:grid; gap:8px; align-content:start;
  }
  .addr{
    padding:10px 12px; border:1px solid ${({theme})=>theme.border}; border-radius:${({theme})=>theme.radius.md}px;
    cursor:pointer; background:${({theme})=>theme.body};
    outline:none;
  }
  .addr:focus-visible{ outline:2px solid ${({theme})=>theme.accent}; outline-offset:2px; }
  .addr.active{ outline:2px solid ${({theme})=>theme.accent}; }
  .map{
    height: 440px; border:1px solid ${({theme})=>theme.border};
    border-radius:${({theme})=>theme.radius.lg}px; overflow:hidden;
    background:${({theme})=>theme.surface};
  }
`;

/* простые локальные иконки */
const Icon = ({name}) => {
  const map = {
    metal:   <path d="M3 7h18v4H3V7zm0 6h18v4H3v-4zm3-9h12l2 3H4l2-3z" />,
    furniture: <path d="M4 10h16v6h-2v4h-2v-4H8v4H6v-4H4v-6zm2-4h12v3H6V6z" />,
    hpl:     <path d="M4 6h10l6 6v6H4V6zm10 0v6h6" />,
    delivery:<path d="M3 7h10v6H3V7zm10 2h4l3 3v3h-7V9zM6 18a2 2 0 110-4 2 2 0 010 4z" />,
    weld:    <path d="M3 12h8v2H3v-2zm10-6h2v12h-2V6zm3 2l5 4-5 4V8z" />,
    paint:   <path d="M4 10l6-6 6 6v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8zm6-2l4 4H6l4-4z" />,
    cam:     <path d="M4 8h9l3-2h4v10h-4l-3-2H4z" />,
    acs:     <path d="M7 3h10v6H7V3zm-2 8h14v10H5V11zm4 2v2h6v-2H9z" />,
    gate:    <path d="M3 20V6l9-3 9 3v14h-2V8l-7-2-7 2v12H3z" />,
    bolt:    <path d="M11 2l6 6-6 6-6-6 6-6zm1 14h-2l-1 6h4l-1-6z" />,
    water:   <path d="M12 2s6 8 6 12a6 6 0 11-12 0c0-4 6-12 6-12z" />,
    smoke:   <path d="M4 18h16v2H4v-2zm2-4h12v2H6v-2zm2-4h8v2H8V10z" />,
    vent:    <path d="M12 4a8 8 0 100 16 8 8 0 000-16zm1 7h5a6 6 0 01-5 5v-5zM6 11h5V6a6 6 0 00-5 5z" />,
    drill:   <path d="M3 10h10l3 3H9l-2 2H3v-5zm13-3h5v3h-5V7z" />
  };
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{map[name]||map.metal}</svg>;
};

export default function HomePage() {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const meta = usePageMeta('home');

  // leaflet иконка один раз через useMemo, чтобы не пересоздавать
  const markerIcon = useMemo(()=> L.icon({
    iconUrl: '/leaflet/marker-icon.png',
    iconRetinaUrl: '/leaflet/marker-icon-2x.png',
    shadowUrl: '/leaflet/marker-shadow.png',
    iconSize: [25,41], iconAnchor: [12,41], popupAnchor: [1,-34], shadowSize: [41,41]
  }), []);

  const SYSTEMS = [
    { k: 'cctv',   title: 'Відеоспостереження',           to: '/systems#cctv'   },
    { k: 'acs',    title: 'Контроль доступу',             to: '/systems#acs'    },
    { k: 'gates',  title: 'Ворота та автоматика',         to: '/systems#gates'  },
    { k: 'electro',title: 'Електромонтаж',                to: '/systems#electro'},
    { k: 'water',  title: 'Водопостачання та опалення',   to: '/systems#water'  },
    { k: 'k1k2',   title: 'К1/К2 димовидалення',          to: '/systems#k1k2'   },
    { k: 'vent',   title: 'Вентиляція',                   to: '/systems#vent'   },
    { k: 'core',   title: 'Алмазне свердління',           to: '/systems#core'   },
  ];

  const PROJECTS = [
    { img:'/img/projects/p1.jpg', cap:'Лінія подачі руди', to:'/metal' },
    { img:'/img/projects/p2.jpg', cap:'Вал Ø420×5200',     to:'/metal' },
    { img:'/img/projects/p3.jpg', cap:'Гейтування складу', to:'/systems#gates' },
    { img:'/img/projects/p4.jpg', cap:'CCTV 64+ камер',    to:'/systems#cctv' },
  ];

  const BLOG_CARDS = [
    {
      id: 'cctv',
      title: 'Як обрати систему відеонагляду',
      lead: 'Ключові параметри камер, мережі та запису.',
      img: '/images/blog/security.svg',
      alt: 'Схематичне зображення камер відеоспостереження'
    },
    {
      id: 'hpl',
      title: 'HPL у комерційних інтер’єрах',
      lead: 'Переваги матеріалу, типові формати та рекомендації по догляду.',
      img: '/images/blog/hpl-design.svg',
      alt: 'Декоративні HPL панелі у просторі'
    },
    {
      id: 'metal',
      title: 'Відновлення валів наплавленням',
      lead: 'Технологія, контроль якості та економія часу у виробництві.',
      img: '/images/blog/metal-process.svg',
      alt: 'Металевий вал під час наплавлення'
    }
  ];

  const INDUSTRIES = [
    { h:'Гірничо-збагачувальні', p:'Вали, вінці, бандажі, редуктори.', icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17l7-12 4 7 3-5 4 10H3z"/></svg>
    )},
    { h:'Агро', p:'Транспорт і вузли зернопереробки.', icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 17a4 4 0 118 0H3zm10-8a5 5 0 018 4h-8V9z"/></svg>
    )},
    { h:'Логістика/склади', p:'Ворота, автоматика, відео.', icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 7l9-4 9 4v10H3V7zm4 2h10v8H7V9z"/></svg>
    )},
    { h:'FMCG', p:'Лінії, каркаси, нестандартні вироби.', icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 6h16v4H4V6zm2 6h12v6H6v-6z"/></svg>
    )},
  ];

  const FAQ = [
    { q:'Як передати креслення?', a:'Надішліть DXF/DWG/STEP/PDF через форму контактів або email — ми підтвердимо отримання і задаємо уточнення.' },
    { q:'Які терміни виготовлення?', a:'Залежить від завантаження — типово 5–20 робочих днів. Швидше — за окремою домовленістю.' },
    { q:'Чи є гарантія?', a:'Так, на роботи та покриття діє гарантія. Деталі в договорі.' },
  ];

  const CERTS = ['ISO 9001', 'CE', 'ДСТУ відповідність', 'Пожежна ліцензія'];

  const LOCATIONS = [
    { id:'kyiv', name:'Офіс — Київ',          pos:[50.4501, 30.5234], addr:'м. Київ, офіс' },
    { id:'brovary', name:'Виробництво меблів — Бровари', pos:[50.5111, 30.7909], addr:'м. Бровари, виробництво меблів' },
    { id:'cherkasy', name:'Металообробка — Черкаси',     pos:[49.4444, 32.0598], addr:'м. Черкаси, металообробка' },
  ];

  const [openFaq, setOpenFaq] = useState(-1);
  const [showAllFaq, setShowAllFaq] = useState(false);

  // карта: активный адрес + ссылка на экземпляр карты
  const [activeIdx, setActiveIdx] = useState(0);
  const mapRef = useRef(null);

  // AOS init — отключаем если reduced motion
  useEffect(() => {
    if (prefersReducedMotion) return;
    // инициализируем после рендера, можно «лениво»
    const id = window.requestIdleCallback
      ? window.requestIdleCallback(() => AOS.init({ once: true, duration: 700, offset: 80, easing: 'ease-out-cubic' }))
      : setTimeout(() => AOS.init({ once: true, duration: 700, offset: 80, easing: 'ease-out-cubic' }), 0);
    return () => {
      window.cancelIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id);
    };
  }, [prefersReducedMotion]);

  // mailto для мини-формы
  const onMiniSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const name = fd.get('name') || '';
    const email = fd.get('email') || '';
    const msg = fd.get('msg') || '';
    const subject = encodeURIComponent(`Запит з сайту від ${name}`);
    const body = encodeURIComponent(`${msg}\n\nВід: ${name} <${email}>`);
    window.location.href = `mailto:info@momv.com.ua?subject=${subject}&body=${body}`;
  };

  const BUY_METALS = ['Сталь конструкційна', 'Нержавіюча сталь', 'Алюміній', 'Мідь', 'Латунь', 'Чавун', 'Профіль/труба', 'Лист гарячекатаний/холоднокатаний'];
  const BUY_WOOD   = ['Дуб', 'Ясень', 'Сосна', 'Бук', 'Фанера', 'МДФ/HDF', 'HPL-панелі', 'Шпон/масивні щити'];

  // helpers для адресов
  const focusLocation = (idx) => {
    setActiveIdx(idx);
    const point = LOCATIONS[idx];
    if (mapRef.current && point?.pos) {
      mapRef.current.flyTo(point.pos, 10, { duration: prefersReducedMotion ? 0 : 0.8 });
    }
  };

  return (
    <>
     <SeoMeta
       title={meta.title}
       description={meta.description}
       keywords={meta.keywords}
       image={meta.image}
       path={meta.path || '/'}
     />
     {/* HERO */}
     <Hero data-motion={prefersReducedMotion ? 'off' : 'on'}>
      <div className="bg" aria-hidden />
      <div className="blob b1" aria-hidden />
      <div className="blob b2" aria-hidden />
      <div className="blob b3" aria-hidden />

      <div className="wrap">
        <div data-aos="fade-up">
          <h1>{t('home.hero.title')}</h1>
          <p className="lead">{t('home.hero.lead')}</p>

          <div className="cta">
            <Link className="btn btn--primary" to="/contacts">{t('home.cta.talk')}</Link>
            <Link className="btn btn--ghost" to="/metal">{t('home.cta.metal')}</Link>
          </div>

          {/* Лента направлений с анимированными иконками */}
          <div className="directions" aria-label="Основні напрямки">
            {[
              { k:'metal', label:t('home.services.items.metal.title') , svg:<path d="M3 7h18v4H3V7zm0 6h18v4H3v-4z" /> },
              { k:'weld',  label:t('home.services.items.weld.title')  , svg:<path d="M3 12h8v2H3v-2zm10-6h2v12h-2z" /> },
              { k:'hpl',   label:t('home.services.items.hpl.title')   , svg:<path d="M4 6h10l6 6v6H4V6z" /> },
              { k:'furniture', label:t('home.services.items.mebli.title'), svg:<path d="M4 10h16v6H4zM6 6h12v3H6z" /> },
              { k:'cctv',  label:'CCTV', svg:<path d="M3 10h10l2 3H3z" /> },
              { k:'acs',   label:'ACS',  svg:<path d="M12 2a5 5 0 00-5 5v3h10V7a5 5 0 00-5-5z" /> },
            ].map((d,i)=>(
              <Link
                to={d.k==='metal'||d.k==='weld'||d.k==='hpl'||d.k==='furniture'? `/${d.k==='furniture'?'mebli':d.k}` : `/systems#${d.k}`}
                key={i}
                className="chip"
                data-aos="zoom-in"
                data-aos-delay={i*50}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">{d.svg}</svg>
                <span>{d.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Прямоугольный мок (вместо телефона) */}
        <div data-aos="fade-left" data-aos-delay="120" style={{display:'grid', placeItems:'center'}}>
          <div className="mock" role="img" aria-label="Превью рішень MOMV">
            <div className="mock-inner">
              <b>{t('home.hero.title')}</b>
              <span>{t('home.hero.lead')}</span>
              <span style={{opacity:.7}}>{t('home.cta.read')}</span>
            </div>
          </div>
        </div>
      </div>
     </Hero>

      {/* NEW 1 — Переваги */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Чому ми</div><h2>Промислова якість та швидкість</h2></div>
          </div>
          <WhyUs>
            {[
              {ico:<Icon name="bolt"/>,  h:'Власне виробництво', p:'Контроль термінів та якості без посередників.'},
              {ico:<Icon name="weld"/>,  h:'Складні роботи', p:'Точне зварювання, токарні та фрезерні операції.'},
              {ico:<Icon name="cam"/>,   h:'Інтеграції', p:'CCTV, КД, ворота, автоматика — під ключ.'},
              {ico:<Icon name="water"/>, h:'Інженерія', p:'Вода, опалення, вентиляція, димовидалення К1/К2.'},
              {ico:<Icon name="delivery"/>, h:'Логістика', p:'Доставка і монтаж по Україні.'},
              {ico:<Icon name="paint"/>, h:'Покриття', p:'Порошок, HPL, шпон, лакофарбові системи.'},
            ].map((it,i)=>(
              <div key={i} className="w" data-aos="fade-up" data-aos-delay={i*60}>
                <div className="ico">{it.ico}</div>
                <b>{it.h}</b>
                <p>{it.p}</p>
              </div>
            ))}
          </WhyUs>
        </div>
      </Section>

      {/* Услуги */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">{t('home.services.kicker')}</div>
              <h2>{t('home.services.title')}</h2>
            </div>
            <Link to="/metal" className="btn btn--ghost">{t('home.viewAll')}</Link>
          </div>

          <Services>
            {[
              { k:'metal',     title:t('home.services.items.metal.title'),     desc:t('home.services.items.metal.desc'),     to:'/metal' },
              { k:'weld',      title:t('home.services.items.weld.title'),      desc:t('home.services.items.weld.desc'),      to:'/metal' },
              { k:'hpl',       title:t('home.services.items.hpl.title'),       desc:t('home.services.items.hpl.desc'),       to:'/hpl'   },
              { k:'furniture', title:t('home.services.items.mebli.title'),     desc:t('home.services.items.mebli.desc'),     to:'/mebli' },
              { k:'paint',     title:t('home.services.items.coat.title'),      desc:t('home.services.items.coat.desc'),      to:'/metal' },
              { k:'delivery',  title:t('home.services.items.delivery.title'),  desc:t('home.services.items.delivery.desc'),  to:'/delivery' },
            ].map((s, i)=>(
              <div className="card" key={s.k} data-aos="fade-up" data-aos-delay={i*60}>
                <div className="icon"><Icon name={s.k} /></div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <Link className="btn btn--ghost" to={s.to}>{t('home.learnMore')}</Link>
              </div>
            ))}
          </Services>
        </div>
      </Section>

      {/* NEW 2 — Ключові показники */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Показники</div><h2>Масштаб та надійність</h2></div>
          </div>
          <KPIs>
            <div className="k" data-aos="zoom-in"><b>15+</b><span>років на ринку</span></div>
            <div className="k" data-aos="zoom-in" data-aos-delay="60"><b>1200+</b><span>виконаних проєктів</span></div>
            <div className="k" data-aos="zoom-in" data-aos-delay="120"><b>24/7</b><span>сервісна підтримка</span></div>
            <div className="k" data-aos="zoom-in" data-aos-delay="180"><b>3</b><span>майданчики виробництва</span></div>
          </KPIs>
        </div>
      </Section>

      {/* Інженерні системи */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Інженерні системи</div>
              <h2>Проєктування, монтаж, сервіс</h2>
            </div>
            <Link to="/systems" className="btn btn--ghost">Детальніше</Link>
          </div>

          <SystemsTeaser>
            {SYSTEMS.map((s, i)=>(
              <div className="card" key={s.k} data-aos="fade-up" data-aos-delay={i*50}>
                <div className="icon" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    {s.k==='cctv' && <path d="M3 10h10l2 3H3v-3zm0-4h14v3H3V6zm16 6l2 2-4 4-2-2 4-4z" />}
                    {s.k==='acs' && <path d="M12 2a5 5 0 00-5 5v3H5v8h14v-8h-2V7a5 5 0 00-5-5zm-3 8V7a3 3 0 116 0v3H9z" />}
                    {s.k==='gates' && <path d="M3 7h4v10H3V7zm14 0h4v10h-4V7zM9 7h6v10H9V7z" />}
                    {s.k==='electro' && <path d="M13 2L4 14h6v8l9-12h-6V2z" />}
                    {s.k==='water' && <path d="M12 3l5 8a5 5 0 11-10 0l5-8z" />}
                    {s.k==='k1k2' && <path d="M4 4h16v4H4V4zm0 6h12v4H4v-4zm14 0h2v10h-2V10z" />}
                    {s.k==='vent' && <path d="M12 3a9 9 0 100 18 9 9 0 000-18zm1 8h6a7 7 0 01-6 6v-6z" />}
                    {s.k==='core' && <path d="M4 12l8-8 8 8-8 8-8-8zm5-1l3 3 5-5" />}
                  </svg>
                </div>
                <h3 style={{margin:0}}>{s.title}</h3>
                <p>Монтаж, налагодження та інтеграція під ключ.</p>
                <div style={{marginTop:'auto'}}>
                  <Link className="btn btn--ghost" to={s.to}>Перейти</Link>
                </div>
              </div>
            ))}
          </SystemsTeaser>
        </div>
      </Section>

      {/* NEW 3 — Пакети рішень */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Комплекти</div><h2>Готові рішення для старту</h2></div>
          </div>
          <Offers>
            <div className="o" data-aos="fade-up">
              <b>Склад «Старт»</b>
              <div className="price">від 49 000 ₴</div>
              <ul>
                <li>Автоматика воріт + 4 IP-камери</li>
                <li>Базовий контроль доступу</li>
                <li>Монтаж і пусконалагодження</li>
              </ul>
              <Link to="/systems#gates" className="btn btn--ghost" style={{marginTop:'auto'}}>Деталі</Link>
            </div>
            <div className="o" data-aos="fade-up" data-aos-delay="80">
              <b>Офіс «Безпека»</b>
              <div className="price">від 32 000 ₴</div>
              <ul>
                <li>КД на 2 точки + домофон</li>
                <li>8 IP-камер + рекордер</li>
                <li>Налаштування хмарного доступу</li>
              </ul>
              <Link to="/systems#acs" className="btn btn--ghost" style={{marginTop:'auto'}}>Деталі</Link>
            </div>
            <div className="o" data-aos="fade-up" data-aos-delay="160">
              <b>Цех «Інженерія»</b>
              <div className="price">індивідуально</div>
              <ul>
                <li>Електромонтаж силових ліній</li>
                <li>Вентиляція + К1/К2</li>
                <li>Опалення/водопостачання</li>
              </ul>
              <Link to="/systems#electro" className="btn btn--ghost" style={{marginTop:'auto'}}>Деталі</Link>
            </div>
          </Offers>
        </div>
      </Section>

      {/* Ми закуповуємо */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Закупівлі</div>
              <h2>Ми закуповуємо</h2>
            </div>
          </div>
          <BuyBlock />
        </div>
      </Section>

      {/* Проекти */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">Проекти</div>
              <h2>Останні реалізації</h2>
            </div>
            <Link to="/articles" className="btn btn--ghost">Кейси та статті</Link>
          </div>

          <Swiper
            modules={[Autoplay, Pagination]}
            autoplay={prefersReducedMotion ? false : { delay: 2800, disableOnInteraction: false, pauseOnMouseEnter: true }}
            pagination={{ clickable: true }}
            spaceBetween={12}
            slidesPerView={3}
            breakpoints={{ 0:{slidesPerView:1}, 680:{slidesPerView:2}, 980:{slidesPerView:3} }}
            aria-label="Останні реалізації"
          >
            {PROJECTS.map((p,i)=>(
              <SwiperSlide key={i}>
                <ProjCard data-aos="zoom-in" data-aos-delay={i%3*70}>
                  <img
                    src={p.img}
                    alt={p.cap}
                    loading="lazy"
                    decoding="async"
                    width="640"
                    height="240"
                  />
                  <div className="cap">
                    <b>{p.cap}</b>
                    <Link to={p.to} className="btn btn--ghost" style={{padding:'6px 10px'}}>Деталі</Link>
                  </div>
                </ProjCard>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </Section>

      {/* Партнёры */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">{t('home.partners.kicker')}</div>
              <h2>{t('home.partners.title')}</h2>
            </div>
          </div>
          <Partners>
            {Array.from({length:8}).map((_,i)=>(
              <div className="partner" key={i} data-aos="zoom-in" data-aos-delay={i*40}>
                {t('home.partners.slot')} #{i+1}
              </div>
            ))}
          </Partners>
        </div>
      </Section>

      {/* Галузі */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Галузі</div><h2>Кому ми допомагаємо</h2></div>
          </div>
          <Industries>
            {INDUSTRIES.map((it,i)=>(
              <div className="i" key={i} data-aos="fade-up" data-aos-delay={i*60}>
                <div className="icon" aria-hidden="true">{it.icon}</div>
                <b>{it.h}</b>
                <p>{it.p}</p>
              </div>
            ))}
          </Industries>
        </div>
      </Section>

      {/* NEW 4 — Тизери статей */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Блог</div><h2>Кейси та статті</h2></div>
            <Link to="/articles" className="btn btn--ghost">Перейти в блог</Link>
          </div>
          <BlogTeasers>
            {BLOG_CARDS.map((item, idx)=>(
              <div className="b" key={item.id} data-aos="fade-up" data-aos-delay={idx*60}>
                <img className="cover" src={item.img} alt={item.alt} loading="lazy" decoding="async" />
                <div className="cap">
                  <b>{item.title}</b>
                  <p>{item.lead}</p>
                  <Link to="/articles" className="btn btn--ghost" style={{justifySelf:'start'}}>Читати</Link>
                </div>
              </div>
            ))}
          </BlogTeasers>
        </div>
      </Section>

      {/* Процесс/этапы */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div>
              <div className="kicker">{t('home.steps.kicker')}</div>
              <h2>{t('home.steps.title')}</h2>
            </div>
          </div>
          <Steps>
            <li data-aos="fade-up"><b>{t('home.steps.s1.title')}</b><p>{t('home.steps.s1.desc')}</p></li>
            <li data-aos="fade-up" data-aos-delay="80"><b>{t('home.steps.s2.title')}</b><p>{t('home.steps.s2.desc')}</p></li>
            <li data-aos="fade-up" data-aos-delay="160"><b>{t('home.steps.s3.title')}</b><p>{t('home.steps.s3.desc')}</p></li>
            <li data-aos="fade-up" data-aos-delay="240"><b>{t('home.steps.s4.title')}</b><p>{t('home.steps.s4.desc')}</p></li>
          </Steps>
        </div>
      </Section>

      {/* NEW 5 — Гарантії та безпека */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Надійність</div><h2>Гарантії та безпека</h2></div>
          </div>
          <Guarantees>
            <div className="g" data-aos="fade-up"><b>12–24 міс. гарантії</b> На обладнання та монтажні роботи згідно договору.</div>
            <div className="g" data-aos="fade-up" data-aos-delay="80"><b>Дані під захистом</b> Проєктна документація та відеоархів — шифрування і доступи за ролями.</div>
            <div className="g" data-aos="fade-up" data-aos-delay="160"><b>Сервіс 24/7</b> SLA для бізнес-критичних об’єктів, виїзд інженера.</div>
          </Guarantees>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">FAQ</div><h2>Часті запитання</h2></div>
          </div>

          <FaqBlock>
            {(showAllFaq ? FAQ : FAQ.slice(0, 3)).map((item, idx) => (
              <div
                className="q"
                key={idx}
                data-aos="fade-up"
                data-aos-delay={idx * 60}
              >
                <button
                  aria-expanded={openFaq === idx}
                  aria-controls={`faq-a-${idx}`}
                  onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                >
                  <span>{item.q}</span>
                  <span aria-hidden="true">{openFaq === idx ? '−' : '+'}</span>
                </button>
                {openFaq === idx && <div id={`faq-a-${idx}`} className="a">{item.a}</div>}
              </div>
            ))}

            {FAQ.length > 3 && (
              <div style={{ marginTop: 12, textAlign: 'center' }}>
                <button
                  className="btn btn--ghost"
                  onClick={() => {
                    setShowAllFaq((v) => !v);
                    setOpenFaq(-1);
                  }}
                >
                  {showAllFaq ? 'Згорнути' : 'Показати всі'}
                </button>
              </div>
            )}
          </FaqBlock>
        </div>
      </Section>

      {/* Мини-форма */}
      <Section>
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Швидкий запит</div><h2>Опишіть задачу — ми відповімо</h2></div>
          </div>
          <MiniForm onSubmit={onMiniSubmit}>
            <input name="name" type="text" placeholder="Ваше ім’я" required aria-label="Ім’я" />
            <input name="email" type="email" placeholder="Email" required aria-label="Email" />
            <textarea name="msg" placeholder="Коротко про задачу / послугу" aria-label="Повідомлення"></textarea>
            <button className="btn btn--primary" type="submit">Відправити</button>
          </MiniForm>
        </div>
      </Section>

      {/* Сертифікації */}
      <Section className="muted">
        <div className="container">
          <div className="section-head">
            <div><div className="kicker">Якість</div><h2>Сертифікації та дозволи</h2></div>
          </div>
          <Certs>
            {CERTS.map((c,i)=><div className="c" key={i}>{c}</div>)}
          </Certs>
        </div>
      </Section>

      {/* Финальный CTA */}
      <CTA>
        <div className="box" data-aos="fade-up">
          <div>
            <h3 style={{margin:0}}>{t('home.cta2.title')}</h3>
            <p style={{margin:0, color:'var(--textMuted)'}}>{t('home.cta2.lead')}</p>
          </div>
          <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
            <Link className="btn btn--primary" to="/contacts">{t('home.cta.talk')}</Link>
            <Link className="btn btn--ghost" to="/articles">{t('home.cta.read')}</Link>
          </div>
        </div>
      </CTA>

      {/* MAP / CONTACT POINTS */}
      <MapWrap>
        <div className="wrap" style={{marginBottom:12}}>
          <div>
            <h2 style={{margin:'0 0 6px'}}>Де ми знаходимося</h2>
            <p style={{margin:0, color:'var(--muted)'}}>Офіс та виробничі майданчики</p>
          </div>
        </div>
        <div className="wrap">
          <div className="panel" role="list" aria-label="Адреси">
            {LOCATIONS.map((p,i)=>(
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                className={`addr ${activeIdx===i ? 'active' : ''}`}
                onClick={()=>focusLocation(i)}
                onKeyDown={(e)=> (e.key==='Enter' || e.key===' ') && focusLocation(i)}
                aria-pressed={activeIdx===i}
                aria-label={`${i+1}. ${p.name}. ${p.addr}`}
              >
                {i+1}. <b>{p.name}</b><br/>{p.addr}
              </div>
            ))}
          </div>
          <div className="map" aria-label="Мапа розташування">
            <MapContainer
              center={LOCATIONS[0].pos}
              zoom={6}
              scrollWheelZoom={false}
              style={{height:'100%', width:'100%'}}
              whenCreated={(map)=>{ mapRef.current = map; }}
            >
              <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {LOCATIONS.map(p=>(
                <Marker key={p.id} position={p.pos} icon={markerIcon}>
                  <Popup><b>{p.name}</b><br/>{p.addr}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>
      </MapWrap>
    </>
  );
}

/* --- маленькие подкомпоненты, чтобы файл был самодостаточным --- */
function BuyBlock(){
  const BUY_METALS = ['Сталь конструкційна', 'Нержавіюча сталь', 'Алюміній', 'Мідь', 'Латунь', 'Чавун', 'Профіль/труба', 'Лист гарячекатаний/холоднокатаний'];
  const BUY_WOOD   = ['Дуб', 'Ясень', 'Сосна', 'Бук', 'Фанера', 'МДФ/HDF', 'HPL-панелі', 'Шпон/масивні щити'];

  return (
    <BuyGrid>
      <div className="col" data-aos="fade-up">
        <div className="icon" aria-hidden>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 4a6 6 0 016 6h-2a4 4 0 00-4-4V6a6 6 0 00-6 6H4a8 8 0 018-8zm0 6a2 2 0 110 4 2 2 0 010-4zm-6 4h2a4 4 0 004 4v2a6 6 0 01-6-6zm10 0h2a6 6 0 01-6 6v-2a4 4 0 004-4z"/></svg>
        </div>
        <h3>Метали</h3>
        <div className="tags">
          {BUY_METALS.map((m,i)=>(<span key={i} className="tag">{m}</span>))}
        </div>
      </div>
      <div className="col" data-aos="fade-up" data-aos-delay="80">
        <div className="icon" aria-hidden>
          <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l5 7h-3l3 5h-4v4h-2v-4H7l3-5H7l5-7z"/></svg>
        </div>
        <h3>Деревина / плити</h3>
        <div className="tags">
          {BUY_WOOD.map((w,i)=>(<span key={i} className="tag">{w}</span>))}
        </div>
      </div>
    </BuyGrid>
  );
}
